import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { MailX, Mail } from 'lucide-react';
import { leadsService } from '../../../../services/leads.service';
import { DocugenLifecycleBadge } from '../../../../components/crm/DocugenLifecycleBadge';
import { PERSONA_LABELS, THEME_LABELS, PLAYBOOK_GROUP_LABELS, labelOf } from '../../playbookLabels';

interface LeadOverview {
  id: number;
  marketingOptOut?: boolean;
  company: string | null;
  email: string | null;
  phone: string | null;
  stage?: { name: string; type?: string | null } | null;
  assignedUser?: { name: string } | null;
  dealValue?: string | number | null;
  nextFollowUpAt?: string | null;
  lastContactAt?: string | null;
  integrationId?: number | null;
  externalCreatedAt?: string | null;
  externalDocsGenerated?: number | null;
  externalDocsUsed?: number | null;
  externalDocsLimit?: number | null;
  externalFirstDocAt?: string | null;
  externalLastActivityAt?: string | null;
  externalTriedLockedFeature?: boolean | null;
  externalTotalTokens?: number | null;
  docugenStage?: string | null;
  playbookGroup?: string | null;
  priorityScore?: number | null;
  jevPersona?: string | null;
  jevTheme?: string | null;
  jevFitProbability?: number | null;
  jevAssessedAt?: string | null;
  holdout?: boolean | null;
}

function formatDate(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }
  try {
    return new Date(value).toLocaleString('pt-BR');
  } catch {
    return '—';
  }
}

function formatMoney(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n)) {
    return `R$ ${value}`;
  }
  return `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
}

export function OverviewTab({ lead }: { lead: LeadOverview }) {
  const queryClient = useQueryClient();
  const optOutMutation = useMutation({
    mutationFn: (next: boolean) => leadsService.update(lead.id, { marketingOptOut: next }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads', lead.id] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success('Lead atualizado');
    },
    onError: () => toast.error('Erro ao atualizar lead'),
  });

  return (
    <div className="space-y-4">
      {lead.integrationId && (
        <div className="-mx-1 rounded-lg border border-border bg-subtle/40">
          <DocugenLifecycleBadge
            lead={{ ...lead, stageType: lead.stage?.type ?? null }}
            variant="timeline"
          />
        </div>
      )}
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <Field label="Empresa" value={lead.company ?? '—'} />
        <Field label="Email" value={lead.email ?? '—'} />
        <Field label="Telefone" value={lead.phone ?? '—'} />
        <Field label="Stage" value={lead.stage?.name ?? '—'} />
        <Field label="Atribuído" value={lead.assignedUser?.name ?? '—'} />
        <Field label="Valor" value={formatMoney(lead.dealValue)} />
        <Field label="Próximo follow-up" value={formatDate(lead.nextFollowUpAt)} />
        <Field label="Última atividade" value={formatDate(lead.lastContactAt)} />
        {lead.docugenStage !== null && lead.docugenStage !== undefined && (
          <>
            <Field label="Grupo" value={labelOf(PLAYBOOK_GROUP_LABELS, lead.playbookGroup)} />
            <Field
              label="Prioridade"
              value={
                lead.priorityScore !== null && lead.priorityScore !== undefined
                  ? String(lead.priorityScore)
                  : '-'
              }
            />
            <Field label="Perfil (Jev)" value={labelOf(PERSONA_LABELS, lead.jevPersona)} />
            <Field label="Tema (Jev)" value={labelOf(THEME_LABELS, lead.jevTheme)} />
            <Field
              label="Software real (Jev)"
              value={
                lead.jevFitProbability !== null && lead.jevFitProbability !== undefined
                  ? `${Math.round(lead.jevFitProbability * 100)}%`
                  : '-'
              }
            />
            <Field label="Avaliado em" value={formatDate(lead.jevAssessedAt)} />
            <Field
              label="Grupo de controle"
              value={lead.holdout ? 'Sim, fica fora das campanhas' : 'Não'}
            />
          </>
        )}
      </dl>
      <button
        type="button"
        onClick={() => optOutMutation.mutate(!lead.marketingOptOut)}
        disabled={optOutMutation.isPending}
        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-border text-ink-secondary hover:bg-subtle disabled:opacity-50"
      >
        {lead.marketingOptOut ? (
          <>
            <Mail className="w-4 h-4" /> Reativar marketing
          </>
        ) : (
          <>
            <MailX className="w-4 h-4" /> Marcar opt-out de marketing
          </>
        )}
      </button>
      {lead.marketingOptOut && (
        <p className="text-xs text-warning">Este lead está fora do mail marketing (opt-out).</p>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-ink-tertiary uppercase tracking-wider">{label}</dt>
      <dd className="text-sm text-ink-primary mt-0.5 break-words">{value}</dd>
    </div>
  );
}
